import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

export interface Track {
  id: number;
  title: string;
  artist: string;
  album: string;
  year: number;
  audioFile: string;
  coverArt: string;
  duration: number;
}

export interface Album {
  artist: string;
  album: string;
  year: number;
  coverArt: string;
  tracks: Track[];
}

interface PlaylistData {
  appleMusic: string;
  tracks: Track[];
}

type ButtonText = 'turn-up' | 'turn-down' | 'okay' | 'got-it';

interface MusicPlayerContextValue {
  playlist: Track[];
  displayedAlbums: Album[];
  currentTrack: Track | null;
  isPlaying: boolean;
  hasUserStartedMusic: boolean;
  isVolumeUp: boolean;
  buttonText: ButtonText;
  progress: number; // 0..1, current playback position within the track
  // True while the full bento widget (MusicPlayer) is mounted somewhere on
  // screen — the mini player hides itself whenever this is true, so the
  // two never show at once.
  fullPlayerVisible: boolean;
  setFullPlayerVisible: (visible: boolean) => void;
  handleAlbumClick: (album: Album) => void;
  handleVolumeToggle: (e: React.MouseEvent) => void;
  togglePlayback: () => void;
}

const MusicPlayerContext = createContext<MusicPlayerContextValue | null>(null);

// Fisher-Yates shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function groupTracksIntoAlbums(tracks: Track[]): Album[] {
  const albumMap = new Map<string, Album>();
  tracks.forEach(track => {
    const key = `${track.artist}-${track.album}`;
    if (!albumMap.has(key)) {
      albumMap.set(key, {
        artist: track.artist,
        album: track.album,
        year: track.year,
        coverArt: track.coverArt,
        tracks: [],
      });
    }
    albumMap.get(key)!.tracks.push(track);
  });
  return Array.from(albumMap.values());
}

// Owns the single <audio> element and all playback state for the whole
// app — mounted once here (in App.tsx, above the router's Routes/
// AnimatePresence remount boundary) so a track keeps playing across
// route changes and HomeV2 panel switches, instead of dying whenever the
// bento widget that used to own it unmounts.
export const MusicPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [playlist, setPlaylist] = useState<Track[]>([]);
  const [displayedAlbums, setDisplayedAlbums] = useState<Album[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasUserStartedMusic, setHasUserStartedMusic] = useState(false);
  const [isVolumeUp, setIsVolumeUp] = useState(false);
  const [buttonText, setButtonText] = useState<ButtonText>('turn-up');
  const [progress, setProgress] = useState(0);
  const [fullPlayerVisible, setFullPlayerVisible] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const fadeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const textTransitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadPlaylist = async () => {
      try {
        const response = await fetch('/content/music/playlist.json');
        const data: PlaylistData = await response.json();
        setPlaylist(shuffleArray(data.tracks));
        const albumList = groupTracksIntoAlbums(data.tracks);
        setDisplayedAlbums(shuffleArray(albumList).slice(0, 9));
      } catch (error) {
        console.error('Error loading playlist:', error);
      }
    };
    loadPlaylist();

    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.volume = 0;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (textTransitionTimeoutRef.current) clearTimeout(textTransitionTimeoutRef.current);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (!hasUserStartedMusic) audio.volume = 0;
    };
    const handleEnded = () => {
      if (hasUserStartedMusic) {
        setCurrentTrackIndex((i) => (i + 1) % playlist.length);
        setIsPlaying(true);
      }
    };
    const handleTimeUpdate = () => {
      setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [currentTrackIndex, playlist.length, hasUserStartedMusic]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          if (err.name !== 'AbortError') console.error('Error playing audio:', err);
        });
      }
    } else {
      audio.pause();
    }
  }, [isPlaying, currentTrackIndex]);

  const handleAlbumClick = useCallback((album: Album) => {
    setHasUserStartedMusic(true);
    const randomTrack = album.tracks[Math.floor(Math.random() * album.tracks.length)];
    setCurrentTrackIndex((prevIndex) => {
      const trackIndex = playlist.findIndex(t => t.id === randomTrack.id);
      return trackIndex !== -1 ? trackIndex : prevIndex;
    });
    setIsPlaying(true);
  }, [playlist]);

  const fadeVolume = useCallback((targetVolume: number, durationMs: number) => {
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);

    const audio = audioRef.current;
    if (!audio) return;

    const startVolume = audio.volume;
    const steps = 50;
    const stepTime = durationMs / steps;
    const volumeStep = (targetVolume - startVolume) / steps;
    let currentStep = 0;

    fadeIntervalRef.current = setInterval(() => {
      currentStep++;
      const newVolume = startVolume + (volumeStep * currentStep);
      if (audio) audio.volume = Math.max(0, Math.min(1, newVolume));
      if (currentStep >= steps && fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    }, stepTime);
  }, []);

  const handleVolumeToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;

    if (textTransitionTimeoutRef.current) clearTimeout(textTransitionTimeoutRef.current);

    if (!hasUserStartedMusic) {
      audio.currentTime = 30;
      audio.volume = 0;
      setIsPlaying(true);
      setHasUserStartedMusic(true);
      setIsVolumeUp(true);
      setButtonText('okay');
      fadeVolume(0.3, 1500);
      textTransitionTimeoutRef.current = setTimeout(() => setButtonText('turn-down'), 800);
    } else if (isVolumeUp) {
      fadeVolume(0, 1000);
      setIsVolumeUp(false);
      setButtonText('got-it');
      textTransitionTimeoutRef.current = setTimeout(() => setButtonText('turn-up'), 800);
    } else {
      fadeVolume(0.3, 1500);
      setIsVolumeUp(true);
      setButtonText('okay');
      textTransitionTimeoutRef.current = setTimeout(() => setButtonText('turn-down'), 800);
    }
  }, [hasUserStartedMusic, isVolumeUp, fadeVolume]);

  // Mini player's play/pause: a real pause (audio.pause()), distinct from
  // the widget's Turn Up/Down volume fade — Turn Down keeps playing at
  // volume 0 rather than stopping, so it isn't reused here.
  const togglePlayback = useCallback(() => {
    setIsPlaying((p) => !p);
  }, []);

  const currentTrack = playlist[currentTrackIndex] || null;

  return (
    <MusicPlayerContext.Provider
      value={{
        playlist,
        displayedAlbums,
        currentTrack,
        isPlaying,
        hasUserStartedMusic,
        isVolumeUp,
        buttonText,
        progress,
        fullPlayerVisible,
        setFullPlayerVisible,
        handleAlbumClick,
        handleVolumeToggle,
        togglePlayback,
      }}
    >
      {currentTrack && <audio ref={audioRef} src={currentTrack.audioFile} />}
      {children}
    </MusicPlayerContext.Provider>
  );
};

export function useMusicPlayer(): MusicPlayerContextValue {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) throw new Error('useMusicPlayer must be used within a MusicPlayerProvider');
  return ctx;
}
